import React, { useState, useEffect } from 'react';
import { Download, Upload, X, Eye, FileText } from 'lucide-react';
import financeService from '@/services/financeService';
import { toast } from 'react-hot-toast';

const TransactionPayment = () => {
  const [approvedTransactions, setApprovedTransactions] = useState([]);
  const [selectedTransaction, setSelectedTransaction] = useState(null);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [loading, setLoading] = useState(false);
  const [uploadedDoc, setUploadedDoc] = useState(null);

  const letterheads = [
    { value: 'puffin_group', label: 'Puffin Group Ltd' },
    { value: 'buildwise', label: 'Buildwise Construction Ltd' },
    { value: 'laser_shield', label: 'Laser Shield Ltd' },
  ];

  useEffect(() => {
    fetchApprovedTransactions();
  }, []);

  const fetchApprovedTransactions = async () => {
    try {
      setLoading(true);
      const response = await financeService.getApprovedTransactions();
      setApprovedTransactions(response.data?.data?.transactions || []);
    } catch (error) {
      console.error('Error fetching approved transactions:', error);
      toast.error('Failed to load approved transactions');
    } finally {
      setLoading(false);
    }
  };

  const getDefaultTemplate = (transaction) => {
    const today = new Date();
    const day = today.getDate().toString().padStart(2, '0');
    const month = (today.getMonth() + 1).toString().padStart(2, '0');
    const year = today.getFullYear();
    const defaultDate = `${day}/${month}/${year}`;

    if (transaction.payment_method === 'cash') {
      return `${defaultDate}

To the Manager,
Finance Department.

Dear Sir,

DEBIT INSTRUCTION

Kindly take this as authorization to process cash payment of [AMOUNT WILL BE AUTO-FILLED] for the following transaction.

Description:        ${transaction.description}
Amount:             [AMOUNT WILL BE AUTO-FILLED]

Thank you

Yours faithfully,
___SIGNATURE_MARKER___

KABIR AL-AMIN ABBA
Group Managing Director`;
    } else {
      return `${defaultDate}

To the Manager,
${transaction.beneficiary_bank || 'Access Bank'}.

Dear Sir,

DEBIT INSTRUCTION

Kindly take this as authorization to debit our account number 1876520128 - Puffin Agro Allied & General Services Limited with a total amount of [AMOUNT WILL BE AUTO-FILLED] and transfer to the following beneficiary.

Beneficiary:        ${transaction.beneficiary_name || '[Beneficiary Name]'}
Bank Details:       [BANK DETAILS WILL BE AUTO-FILLED]
Amount:             [AMOUNT WILL BE AUTO-FILLED]

Thank you

Yours faithfully,
___SIGNATURE_MARKER___

KABIR AL-AMIN ABBA
Group Managing Director`;
    }
  };

  const [paymentData, setPaymentData] = useState({
    document_template: '',
    letterhead: 'puffin_group',
  });

  const handleOpenPaymentModal = (transaction) => {
    setSelectedTransaction(transaction);
    setPaymentData({
      document_template: getDefaultTemplate(transaction),
      letterhead: 'puffin_group',
    });
    setUploadedDoc(null);
    setShowPaymentModal(true);
  };

  const handleFileUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        toast.error('File size must be less than 5MB');
        return;
      }
      setUploadedDoc(file);
      toast.success('Document uploaded');
    }
  };

  const handleProcessPayment = async () => {
    try {
      setLoading(true);
      
      const formData = new FormData();
      formData.append('transaction_id', selectedTransaction.id);
      formData.append('document_template', paymentData.document_template);
      formData.append('letterhead', paymentData.letterhead);
      if (uploadedDoc) {
        formData.append('supporting_document', uploadedDoc);
      }

      await financeService.processTransactionPayment(formData);
      
      toast.success('Payment processed successfully!');
      setShowPaymentModal(false);
      fetchApprovedTransactions();
    } catch (error) {
      console.error('Error processing payment:', error);
      toast.error('Failed to process payment');
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    return '₦' + new Intl.NumberFormat('en-NG').format(amount);
  };

  return (
    <div className="p-6">
      <div className="mb-6">
        <h2 className="text-2xl font-bold text-gray-900">Transaction Payment Processing</h2>
        <p className="text-gray-600 mt-1">Process payments for GMD-approved transactions</p>
      </div>

      {loading && !showPaymentModal ? (
        <div className="text-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="text-gray-600 mt-4">Loading transactions...</p>
        </div>
      ) : approvedTransactions.length === 0 ? (
        <div className="bg-white rounded-lg shadow p-12 text-center">
          <FileText className="h-16 w-16 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">No Approved Transactions</h3>
          <p className="text-gray-600">There are no GMD-approved transactions ready for payment processing.</p>
        </div>
      ) : (
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">ID</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Description</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Amount</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Payment Method</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Beneficiary</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Action</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {approvedTransactions.map((transaction) => (
                <tr key={transaction.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                    #{transaction.id}
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-900">{transaction.description}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-bold text-green-600">
                    {formatCurrency(transaction.amount)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    {transaction.payment_method === 'cash' ? (
                      <span className="px-2 py-1 text-xs font-semibold rounded-full bg-green-100 text-green-800">
                        CASH
                      </span>
                    ) : (
                      <span className="px-2 py-1 text-xs font-semibold rounded-full bg-blue-100 text-blue-800">
                        TRANSFER
                      </span>
                    )}
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-900">
                    {transaction.payment_method === 'transfer' ? transaction.beneficiary_name : 'N/A'}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm">
                    <button
                      onClick={() => handleOpenPaymentModal(transaction)}
                      className="text-blue-600 hover:text-blue-800 font-medium"
                    >
                      Process Payment
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Payment Processing Modal */}
      {showPaymentModal && selectedTransaction && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-full max-w-3xl shadow-lg rounded-md bg-white">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-medium text-gray-900">
                Process Payment - Download Payment Letter
              </h3>
              <button
                onClick={() => setShowPaymentModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="mb-4 p-4 bg-blue-50 border border-blue-200 rounded-md">
              <h4 className="font-medium text-blue-900 mb-3">
                Payment Summary (Read-Only)
              </h4>
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div className="space-y-2">
                  <div>
                    <label className="text-gray-600 text-xs">Transaction ID</label>
                    <p className="font-semibold text-gray-900">#{selectedTransaction.id}</p>
                  </div>
                  <div>
                    <label className="text-gray-600 text-xs">
                      {selectedTransaction.payment_method === 'transfer' ? 'Beneficiary Name' : 'Payment Type'}
                    </label>
                    <p className="font-semibold text-gray-900">
                      {selectedTransaction.payment_method === 'transfer' ? selectedTransaction.beneficiary_name : 'CASH PAYMENT'}
                    </p>
                  </div>
                  <div>
                    <label className="text-gray-600 text-xs">Amount (Cannot be changed)</label>
                    <p className="font-bold text-green-600 text-lg">
                      {formatCurrency(selectedTransaction.amount)}
                    </p>
                  </div>
                </div>
                <div className="space-y-2">
                  <div>
                    <label className="text-gray-600 text-xs">Description</label>
                    <p className="font-semibold text-gray-900">{selectedTransaction.description}</p>
                  </div>
                  {selectedTransaction.payment_method === 'transfer' && (
                    <div>
                      <label className="text-gray-600 text-xs">Bank Details (Cannot be changed)</label>
                      <p className="font-semibold text-gray-900 text-xs bg-white p-2 rounded border border-gray-200">
                        {selectedTransaction.beneficiary_bank} - {selectedTransaction.beneficiary_account}
                      </p>
                    </div>
                  )}
                </div>
              </div>
            </div>

            <div className="space-y-4">
              {/* Document Upload */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Upload Supporting Document (Optional)
                </label>
                <div className="flex items-center gap-3">
                  <label className="flex-1 flex items-center justify-center px-4 py-3 border-2 border-dashed border-gray-300 rounded-lg cursor-pointer hover:border-blue-500 hover:bg-blue-50 transition-colors">
                    <Upload className="h-5 w-5 text-gray-400 mr-2" />
                    <span className="text-sm text-gray-600">
                      {uploadedDoc ? uploadedDoc.name : 'Click to upload document (PDF, max 5MB)'}
                    </span>
                    <input
                      type="file"
                      accept=".pdf,.doc,.docx"
                      onChange={handleFileUpload}
                      className="hidden"
                    />
                  </label>
                  {uploadedDoc && (
                    <button
                      onClick={() => setUploadedDoc(null)}
                      className="p-2 text-red-600 hover:bg-red-50 rounded"
                    >
                      <X className="h-5 w-5" />
                    </button>
                  )}
                </div>
              </div>

              {/* Document Template Editor */}
              <div>
                <div className="mb-2">
                  <label className="block text-sm font-medium text-gray-700">
                    Payment Letter Content
                  </label>
                  <p className="text-xs text-gray-500 mt-1">
                    This letter will be printed on pre-printed company letterhead.
                  </p>
                </div>
                <textarea
                  value={paymentData.document_template}
                  onChange={(e) => setPaymentData({...paymentData, document_template: e.target.value})}
                  rows={12}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                  placeholder="The default template will be loaded automatically. You can edit it as needed."
                />

                <div className="mt-2 p-3 bg-yellow-50 border border-yellow-200 rounded-md">
                  <p className="text-xs text-yellow-800 font-medium">
                    ⚠️ Protected Information (Auto-included in PDF):
                  </p>
                  <ul className="text-xs text-yellow-700 mt-1 ml-4 list-disc">
                    <li>
                      Amount:{" "}
                      <strong>
                        N{new Intl.NumberFormat('en-NG', {
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2,
                        }).format(selectedTransaction.amount)}
                      </strong>{" "}
                      (Cannot be changed)
                    </li>
                    {selectedTransaction.payment_method === 'transfer' && (
                      <li>
                        Bank Details:{" "}
                        <strong>
                          {selectedTransaction.beneficiary_bank} - {selectedTransaction.beneficiary_account}
                        </strong>{" "}
                        (Cannot be changed)
                      </li>
                    )}
                  </ul>
                  <p className="text-xs text-yellow-700 mt-2">
                    These details will be automatically included in the downloaded PDF and cannot be modified.
                  </p>
                </div>
              </div>

              {/* Next Step Info */}
              <div className="bg-blue-50 border border-blue-200 rounded p-3">
                <p className="text-sm font-medium text-blue-800">
                  ℹ️ After downloading the payment letter:
                </p>
                <ul className="text-sm text-blue-700 mt-1 ml-4 list-disc">
                  <li>Process the payment through your bank</li>
                  <li>Transaction will be marked as completed</li>
                  <li>Keep the payment reference for records</li>
                </ul>
              </div>
            </div>

            <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200 mt-6">
              <button
                onClick={() => setShowPaymentModal(false)}
                className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleProcessPayment}
                disabled={loading}
                className="flex items-center space-x-2 px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 disabled:opacity-50"
              >
                {loading ? (
                  <span>Processing...</span>
                ) : (
                  <>
                    <Download className="h-4 w-4" />
                    <span>Process Payment & Download Letter</span>
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TransactionPayment;
